import assert from 'assert';
import { MomentModel, JobModel } from '../../entities';
import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import { action, computed, observable, makeObservable } from 'mobx';
import { closeWindow, openWindow } from '../../services/overlays';
import { WindowState } from '../overlays/windowState';

type Props = {
  // jobId: string
}

export type OpenArgs = {
  jobId: string;
  clipList: MomentModel[];
  multipleActiveTrackTypes: string[];
}

export class JobErrorsWindowState
  extends StoreNode {

  readonly nodeType: 'JobErrorsWindow' = 'JobErrorsWindow';

  constructor(store: Store, _props?: Props) {
    super(store);
    makeObservable(this, {
      jobId: observable,
      isVisible: observable,
      clipList: observable,
      multipleActiveTrackTypes: observable,
      isLoading: observable,
      job: computed,
      uniqueActiveTrackTypes: computed,
      clipErrorCount: computed,
      trackError: computed,
      trackErrorCount: computed,
      clipError: computed,
      errorCount: computed,
      multipleErrors: computed,
      showClipError: computed,
      cancel: action,
      open: action,
      clearState: action,
      close: action,
      onWindowEntered: action,
      onWindowExited: action,
    });
    this.window.listen(
      this.windowListener);
  }
  jobId: string | null = null;
  isVisible = false;
  clipList: MomentModel[] = [];
  multipleActiveTrackTypes: string[] = [];
  isLoading: boolean = false;

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        if (this.isLoading)
          return;
        this.close('close');
        break;
    }
  }
  readonly window = new WindowState(this.store);

  get uniqueActiveTrackTypes(): string[] {
    return [...new Set(this.multipleActiveTrackTypes)];
  }

  get clipErrorCount() {
    return this.clipList.length ?? 0;
  }

  get trackError() {
    return this.multipleActiveTrackTypes.length > 0;
  }

  get trackErrorCount() {
    return this.multipleActiveTrackTypes.length;
  }

  get clipError() {
    return this.clipErrorCount > 0;
  }

  get errorCount() {
    return this.showClipError ? this.clipErrorCount : this.trackErrorCount;
  }

  get multipleErrors() {
    return this.errorCount > 1;
  }

  get showClipError() {
    return this.clipError && !this.trackError;
  }

  /** Hides the window and clears the entire state of the window. */
  cancel = () => {
    if (this.isLoading)
      return;

    this.close('close');
  }

  open(args: OpenArgs) {
    const { jobId, clipList, multipleActiveTrackTypes } = args;
    this.jobId = jobId;
    assert(this.jobId,
      `Cannot show a JobErrorsWindow without a valid jobId`);

    this.isLoading = true;

    this.isVisible = true;
    this.clipList = clipList;
    this.multipleActiveTrackTypes = multipleActiveTrackTypes;

    openWindow(this, 'JobErrorsWindow');
    this.emit('open');
  }

  clearState = () => {
    this.jobId = null;
    this.clipList = [];
    this.multipleActiveTrackTypes = [];
  }

  close(msg?: string) {
    this.isVisible = false;
    closeWindow(this);
    if (msg)
      this.emit(msg);
  }

  onWindowEntered = () => {
    this.isLoading = false;
  }

  onWindowExited = () => {
    if (!this.isVisible)
      this.clearState();
  }
}